import { Form } from 'antd';

/**
 * Keys, permission states and watched values of the calculation, allowance and variation fields.
 */
export default function useCompanyCalculationFields({
  INPUT_KEYS,
  form,
  getFieldState,
  isWorkFromHomeChecked,
  typeOfCompany,
}) {
  // * Calculation settings data form
  const bonusKeys = INPUT_KEYS.calculationSettings.bonus;
  const bonusState = getFieldState(bonusKeys);

  const otherIncomeKeys = INPUT_KEYS.calculationSettings.otherIncome;
  const otherIncomeState = getFieldState(otherIncomeKeys);

  const suspensionKeys = INPUT_KEYS.calculationSettings.suspension;
  const suspensionState = getFieldState(suspensionKeys);

  const otherExpensesKeys = INPUT_KEYS.calculationSettings.otherExpenses;
  const otherExpensesState = getFieldState(otherExpensesKeys);

  const workTypesKeys = INPUT_KEYS.calculationSettings.workTypes;
  const workTypesState = getFieldState(workTypesKeys);

  const overTime2Keys = INPUT_KEYS.calculationSettings.overTime2;
  const overTime2State = getFieldState(overTime2Keys);

  const extendedAbExportKeys = INPUT_KEYS.calculationSettings.extendedAbExport;
  const extendedAbExportState = getFieldState(extendedAbExportKeys);
  // Firma-specifični "extended" workType-ovi (rdWork/UNLIMIT OT/AI Digital) se
  // nude u workType picker-u SAMO kad je extended AB export uključen — inače
  // ostaju van da ne zagade picker ostalih firmi.
  const extendedAbExportChecked = Form.useWatch(extendedAbExportKeys, form);

  const bonusChecked = Form.useWatch(bonusKeys, form);
  const otherIncomeChecked = Form.useWatch(otherIncomeKeys, form);
  const suspensionChecked = Form.useWatch(suspensionKeys, form);
  const otherExpensesChecked = Form.useWatch(otherExpensesKeys, form);

  // * Salary Calculation Settings data form
  const salaryCalculationTypeKeys =
    INPUT_KEYS.salaryCalculationSettings.salaryCalculationType;
  const salaryCalculationTypeState = getFieldState(salaryCalculationTypeKeys);

  const holidayLeaveConfirmationKeys =
    INPUT_KEYS.salaryCalculationSettings.holidayLeaveConfirmation;
  const holidayLeaveConfirmationState = getFieldState(
    holidayLeaveConfirmationKeys
  );

  const sickLeaveUpTo30Days65ConfirmationKeys =
    INPUT_KEYS.salaryCalculationSettings.sickLeaveUpTo30Days65Confirmation;
  const sickLeaveUpTo30Days65ConfirmationState = getFieldState(
    sickLeaveUpTo30Days65ConfirmationKeys
  );

  const sickLeaveUpTo30Days100ConfirmationKeys =
    INPUT_KEYS.salaryCalculationSettings.sickLeaveUpTo30Days100Confirmation;
  const sickLeaveUpTo30Days100ConfirmationState = getFieldState(
    sickLeaveUpTo30Days100ConfirmationKeys
  );

  const paidLeaveConfirmationKeys =
    INPUT_KEYS.salaryCalculationSettings.paidLeaveConfirmation;
  const paidLeaveConfirmationState = getFieldState(paidLeaveConfirmationKeys);

  const publicHolidayConfirmationKeys =
    INPUT_KEYS.salaryCalculationSettings.publicHolidayConfirmation;
  const publicHolidayConfirmationState = getFieldState(
    publicHolidayConfirmationKeys
  );

  const salaryCalculationType = Form.useWatch(salaryCalculationTypeKeys, form);

  // * Allowances data form
  const mealAllowanceKeys = INPUT_KEYS.allowances.mealAllowance;
  const mealAllowanceState = getFieldState(mealAllowanceKeys);

  const holidayAllowanceKeys = INPUT_KEYS.allowances.holidayAllowance;
  const holidayAllowanceState = getFieldState(holidayAllowanceKeys);

  const travelAllowanceKeys = INPUT_KEYS.allowances.travelAllowance;
  const travelAllowanceState = getFieldState(travelAllowanceKeys);

  const workFromHomeAllowanceKeys = INPUT_KEYS.allowances.workFromHomeAllowance;
  const workFromHomeAllowanceState = getFieldState(workFromHomeAllowanceKeys);

  // * Currencies data form
  const mealAllowanceCurrencyKeys = INPUT_KEYS.currencies.mealAllowanceCurrency;
  const mealAllowanceCurrencyState = getFieldState(mealAllowanceCurrencyKeys);

  const holidayAllowanceCurrencyKeys =
    INPUT_KEYS.currencies.holidayAllowanceCurrency;
  const holidayAllowanceCurrencyState = getFieldState(
    holidayAllowanceCurrencyKeys
  );

  const travelAllowanceCurrencyKeys =
    INPUT_KEYS.currencies.travelAllowanceCurrency;
  const travelAllowanceCurrencyState = getFieldState(
    travelAllowanceCurrencyKeys
  );

  const workFromHomeAllowanceCurrencyKeys =
    INPUT_KEYS.currencies.workFromHomeAllowanceCurrency;
  const workFromHomeAllowanceCurrencyState = getFieldState(
    workFromHomeAllowanceCurrencyKeys
  );

  // * Currencies data form
  const otherIncomeCurrencyKeys = INPUT_KEYS.otherAllowances.otherIncome;
  const otherIncomeCurrencyState = getFieldState(otherIncomeCurrencyKeys);

  const otherExpensesCurrencyKeys = INPUT_KEYS.otherAllowances.otherExpenses;
  const otherExpensesCurrencyState = getFieldState(otherExpensesCurrencyKeys);

  const suspensionCurrencyKeys = INPUT_KEYS.otherAllowances.suspension;
  const suspensionCurrencyState = getFieldState(suspensionCurrencyKeys);

  const bonusCurrencyKeys = INPUT_KEYS.otherAllowances.bonus;
  const bonusCurrencyState = getFieldState(bonusCurrencyKeys);

  // * Bonus Variations
  const bonusVariationsEnabledKeys = INPUT_KEYS.bonusVariations.enabled;
  const bonusVariationsEnabledState = getFieldState(bonusVariationsEnabledKeys);
  const bonusVariationsTypesKeys = INPUT_KEYS.bonusVariations.types;
  const bonusVariationsEnabled = Form.useWatch(
    bonusVariationsEnabledKeys,
    form
  );

  // * Suspension Variations
  const suspensionVariationsEnabledKeys =
    INPUT_KEYS.suspensionVariations.enabled;
  const suspensionVariationsEnabledState = getFieldState(
    suspensionVariationsEnabledKeys
  );
  const suspensionVariationsTypesKeys = INPUT_KEYS.suspensionVariations.types;
  const suspensionVariationsEnabled = Form.useWatch(
    suspensionVariationsEnabledKeys,
    form
  );

  // * Non-Monetary Benefit
  const nonMonetaryBenefitKeys =
    INPUT_KEYS.calculationSettings.nonMonetaryBenefit;
  const nonMonetaryBenefitState = getFieldState(nonMonetaryBenefitKeys);
  const nonMonetaryBenefitChecked = Form.useWatch(nonMonetaryBenefitKeys, form);

  const nonMonetaryBenefitCurrencyKeys =
    INPUT_KEYS.otherAllowances.nonMonetaryBenefit;
  const nonMonetaryBenefitCurrencyState = getFieldState(
    nonMonetaryBenefitCurrencyKeys
  );

  // * Non-Monetary Benefit Variations
  const nonMonetaryBenefitVariationsEnabledKeys =
    INPUT_KEYS.nonMonetaryBenefitVariations.enabled;
  const nonMonetaryBenefitVariationsEnabledState = getFieldState(
    nonMonetaryBenefitVariationsEnabledKeys
  );
  const nonMonetaryBenefitVariationsTypesKeys =
    INPUT_KEYS.nonMonetaryBenefitVariations.types;
  const nonMonetaryBenefitVariationsEnabled = Form.useWatch(
    nonMonetaryBenefitVariationsEnabledKeys,
    form
  );

  // * CONSTANTS
  const hasBranchCompanyType = typeOfCompany === 'branch';
  const hasSalaryCalculationType = salaryCalculationType === 'fixed';
  const shouldWorkFromHomeBeVisible = isWorkFromHomeChecked !== 'none';

  return {
    bonusChecked,
    bonusCurrencyKeys,
    bonusCurrencyState,
    bonusKeys,
    bonusState,
    bonusVariationsEnabled,
    bonusVariationsEnabledKeys,
    bonusVariationsEnabledState,
    bonusVariationsTypesKeys,
    extendedAbExportChecked,
    extendedAbExportKeys,
    extendedAbExportState,
    hasBranchCompanyType,
    hasSalaryCalculationType,
    holidayAllowanceCurrencyKeys,
    holidayAllowanceCurrencyState,
    holidayAllowanceKeys,
    holidayAllowanceState,
    holidayLeaveConfirmationKeys,
    holidayLeaveConfirmationState,
    mealAllowanceCurrencyKeys,
    mealAllowanceCurrencyState,
    mealAllowanceKeys,
    mealAllowanceState,
    nonMonetaryBenefitChecked,
    nonMonetaryBenefitCurrencyKeys,
    nonMonetaryBenefitCurrencyState,
    nonMonetaryBenefitKeys,
    nonMonetaryBenefitState,
    nonMonetaryBenefitVariationsEnabled,
    nonMonetaryBenefitVariationsEnabledKeys,
    nonMonetaryBenefitVariationsEnabledState,
    nonMonetaryBenefitVariationsTypesKeys,
    otherExpensesChecked,
    otherExpensesCurrencyKeys,
    otherExpensesCurrencyState,
    otherExpensesKeys,
    otherExpensesState,
    otherIncomeChecked,
    otherIncomeCurrencyKeys,
    otherIncomeCurrencyState,
    otherIncomeKeys,
    otherIncomeState,
    overTime2Keys,
    overTime2State,
    paidLeaveConfirmationKeys,
    paidLeaveConfirmationState,
    publicHolidayConfirmationKeys,
    publicHolidayConfirmationState,
    salaryCalculationTypeKeys,
    salaryCalculationTypeState,
    shouldWorkFromHomeBeVisible,
    sickLeaveUpTo30Days100ConfirmationKeys,
    sickLeaveUpTo30Days100ConfirmationState,
    sickLeaveUpTo30Days65ConfirmationKeys,
    sickLeaveUpTo30Days65ConfirmationState,
    suspensionChecked,
    suspensionCurrencyKeys,
    suspensionCurrencyState,
    suspensionKeys,
    suspensionState,
    suspensionVariationsEnabled,
    suspensionVariationsEnabledKeys,
    suspensionVariationsEnabledState,
    suspensionVariationsTypesKeys,
    travelAllowanceCurrencyKeys,
    travelAllowanceCurrencyState,
    travelAllowanceKeys,
    travelAllowanceState,
    workFromHomeAllowanceCurrencyKeys,
    workFromHomeAllowanceCurrencyState,
    workFromHomeAllowanceKeys,
    workFromHomeAllowanceState,
    workTypesKeys,
    workTypesState,
  };
}
